import React, { useCallback, useMemo, useState } from "react";
import "./../../styles/homePage/patientsList.scss";
import TablePage from "../../components/patientsList/genericTable/TablePage";
import { useTranslation } from "react-i18next";
import { TableCard } from "../../common/types/tableCardType";
import { useQuery } from "@tanstack/react-query";
import {
  exportPatients,
  getAllPatients,
  getPatientsCardsData,
} from "../../common/api/apiCalls";
import { svgs } from "../../common/assats";
import { useNavigate } from "react-router-dom";
import { debounce } from "@mui/material";
import { GenericRecord } from "../../common/types/filterObjType";
import { DateRange } from "react-day-picker";
import { organizePatientData } from "../../common/function/organizePatientData";
import { getAllSites } from "../../common/api/siteService";
import Loader from "../../components/Loader/Loader";
import { useUser } from "../../common/contexts/UserContext";
import { roles } from "../../common/types/roles";

const sortKeyArray = [
  "firstName",
  "lastName",
  "gender",
  "ear",
  "identityNumber",
  "email",
  "phoneNumber",
  "active",
  "center",
  "relatedHCP",
  "created",
];

const PatientsList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useUser();

  const [search, setSearch] = useState<string>("");
  const [debouncedSearch, setDebouncedSearch] = useState<string>(""); // Debounced value for API
  const [sortKey, setSortKey] = useState<string>("");
  const [sortOrder, setSortOrder] = useState<"ASC" | "DESC">("DESC");
  const [filters, setFilters] = useState<string[]>([]);
  const [selectedRange, setSelectedRange] = useState<DateRange | undefined>(
    undefined
  );

  const getText = (text: string) => t(`patientsList.${text}`);

  const {
    data: usersList,
    error: patientsListError,
    isLoading: isLoadingPatients,
  } = useQuery({
    queryKey: [
      "usersData",
      sortKey,
      sortOrder,
      debouncedSearch,
      filters,
      selectedRange,
    ],
    queryFn: () =>
      getAllPatients(
        sortKey,
        sortOrder,
        debouncedSearch,
        filters,
        selectedRange
      ),
  });
  // Second query, dependent on the first one
  const {
    data: filtersData,
    error: fetchCenterDataError,
    isLoading: isCenterDataLoading,
  } = useQuery({
    queryKey: ["centerData"],
    queryFn: getAllSites,
  });

  const {
    data: cardsData,
    error: patientsCardsError,
    isLoading: isLoadingPatientsCards,
  } = useQuery({
    queryKey: ["cards"],
    queryFn: getPatientsCardsData,
  });

  // Debounce the search input
  const debouncedSetSearch = useCallback(
    debounce((value: string) => {
      setDebouncedSearch(value);
    }, 300), // 300ms delay
    []
  );

  // Update the search value when typing
  const handleSearchChange = (str: string) => {
    setSearch(str); // Instant feedback for the user
    debouncedSetSearch(str); // Delayed API request
  };
  const cards: TableCard[] = useMemo(
    () =>
      user.role !== roles.HCP
        ? [
            {
              headline: getText("cards.Patients"),
              icon: svgs.cards.Patients,
              value: isLoadingPatientsCards ? 0 : cardsData[2] || 0,
              action: () => {
                console.log("click");
              },
            },
            {
              headline: getText("cards.Centers"),
              icon: svgs.cards.Centers,
              value: isLoadingPatientsCards ? 0 : cardsData[1] || 0,
              action: () => {
                console.log("click");
              },
            },
            {
              headline: getText("cards.Average_new_patients_per_week"),
              icon: svgs.cards.ActiveUsers,
              value: isLoadingPatientsCards ? 0 : cardsData[0] || 0,
              action: () => {
                console.log("click");
              },
            },
          ]
        : [
            {
              headline: getText("cards.Patients"),
              icon: svgs.cards.Patients,
              value: isLoadingPatientsCards ? 0 : cardsData[2] || 0,
              action: () => {
                console.log("click");
              },
            },
            {
              headline: getText("cards.Average_new_patients_per_week"),
              icon: svgs.cards.ActiveUsers,
              value: isLoadingPatientsCards ? 0 : cardsData[0] || 0,
              action: () => {
                console.log("click");
              },
            },
          ],
    [cardsData]
  );
  const handleExport = async () => {
    try {
      const blob = await exportPatients({
        sortKey,
        sortOrder,
        search: debouncedSearch,
        filters,
      });
      // Generate a downloadable URL and trigger the download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "Patients_List.xlsx");
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url); // Cleanup URL
    } catch (error) {
      console.error("Error exporting devices:", error);
      alert("Failed to export devices. Please try again.");
    }
  };
  function getFilters(data: { id: string; SiteName: string }[]) {
    if (!data || !Array.isArray(data)) {
      console.error("Invalid data passed to getFilters:", data);
      return {};
    }
    // Filter object for the table
    let filterObj: GenericRecord<string> = {
      center: data.map((val) => {
        return { name: val.SiteName, value: val.SiteName };
      }),
      status: [
        { name: "Active", value: "1" },
        { name: "inactive", value: "0" },
      ],
    };
    return filterObj;
  }
  function handleRowClick(index: number) {
    navigate("EditPatient", {
      state: { user: usersList[index] },
    });
  }
  if (isCenterDataLoading || isLoadingPatientsCards) {
    return <Loader text="Loading..." />;
  }
  if (patientsListError || fetchCenterDataError || patientsCardsError) {
    return <h1>Error</h1>;
  }
  return (
    <>
      <TablePage
        subButtonText={getText("export")}
        subAction={handleExport}
        dateFilter={{ setSelectedRange, selectedRange }}
        filterObj={filtersData ? getFilters(filtersData) : {}}
        tableHeadline={getText(`patientsList`)}
        headerHeadline={getText("patientsList")}
        cards={cards}
        trashAction={() => {}}
        filters={filters}
        setFilters={setFilters}
        searchValue={search}
        setSearchValue={handleSearchChange}
        searchPlaceholder={getText("filter.Search_placeholder")}
        setSortKey={setSortKey}
        sortKey={sortKey}
        setSortOrder={setSortOrder}
        sortOrder={sortOrder}
        tableData={
          isLoadingPatients ? [] : organizePatientData(usersList) || []
        }
        sortKeysArray={sortKeyArray.map((value) => {
          return { name: getText(`sort.${value}`), val: value };
        })}
        rowClick={handleRowClick}
        EndRowIcon={svgs.icons.Eye}
      />
    </>
  );
};

export default PatientsList;
